import CustomerOrder from '@/api/restaurant-sale/customer-order'

import { baseState, baseMutations } from '@/store/mixins'
let isFetchingBefore = false // lock variable outside the action
let fetchProductsPromise = null

export const state = {
  ...baseState,
  productCategories: [],
  products: [],
  systemSettings: [],
  promotions: {},
  companyInfo: {},
  tableSelected: {}
}

export const getters = {
  defaultPrice: state => (productId, guest_level_id, uom_id) => {
    if (!productId) return 0

    const product = state.products.find(p => p.product_id === productId)
    if (!product) return 0

    // get default uom
    let default_uom_id = uom_id
    if (!default_uom_id) {
      const defaultUom = product.sale_code.find(s => s.unit_of_measure && s.unit_of_measure.is_default)
      default_uom_id = defaultUom ? defaultUom.uom_id : null
    }
    if (!default_uom_id) return 0

    // base unit price
    const unitObj = product.sale_code.find(s => s.uom_id === default_uom_id)
    const unitPrice = unitObj && unitObj.product_price ? unitObj.product_price.sale_price : 0

    // check add up price
    const addUp = product.product_price_scheme.find(
      s => s.uom_id === default_uom_id && s.guest_level_id === guest_level_id
    )

    if (addUp) {
      const addUpAmount =
        addUp.increment_type === "%"
          ? unitPrice * (addUp.increment_amount / 100)
          : addUp.increment_amount
      return _.ceil(unitPrice + addUpAmount, 2)
    }

    return _.ceil(unitPrice, 2)
  },
  productPriceScheme: state => (productId, guest_level_id, uom_id) => {
    if (!productId) return 0

    const product = state.products.find(p => p.product_id === productId)
    if (!product) return 0
    // check add up price
    const scheme = product.product_price_scheme.find(
      s => s.uom_id === uom_id && s.guest_level_id === guest_level_id
    )

    const unitObj = product.sale_code.find(s => s.uom_id === uom_id)
    const unitPrice = unitObj && unitObj.product_price ? unitObj.product_price.sale_price : 0

    if (scheme) {
      const addUpAmount =
        scheme.increment_type === "%"
          ? unitPrice * (scheme.increment_amount / 100)
          : scheme.increment_amount
      return {
        unit_price: _.ceil(unitPrice + addUpAmount, 2),
        max_person: scheme.max_person,
        service_length: scheme.service_length,
        is_pre_charge: product.is_pre_charge,
      }
    }

    return {
      unit_price: _.ceil(unitPrice, 2),
      max_person: product.max_person,
      service_length: product.service_length,
      is_pre_charge: product.is_pre_charge,
    }
  },
  saleCodeByDefaultUom: state => productId => {
    if (!productId || !Array.isArray(state.products)) return null

    const product = state.products.find(p => p.product_id === productId)
    if (!product || !Array.isArray(product.sale_code)) return null

    const defaultSaleCode = product.sale_code.find(
      s => s.unit_of_measure && s.unit_of_measure.is_default === true
    )

    return defaultSaleCode || null
  },
  getUomName: state => (productId, uomId) => {
    if (!productId || !Array.isArray(state.products)) return null

    const product = state.products.find(p => p.product_id === productId)
    if (!product || !Array.isArray(product.sale_code)) return null

    const saleCode = product.sale_code.find(
      s => s.unit_of_measure && s.unit_of_measure.uom_id === uomId
    )

    return saleCode ? saleCode.unit_of_measure.uom_name_en : null
  }
  ,
  getProduct: state => productId => {
    if (!productId || !Array.isArray(state.products)) return null
    return state.products.find(p => p.product_id === productId) || null
  },
  getPromotionDiscount: state => (productId, uomId, quantity = 1, guest_level_id = 1) => {
    if (
      !productId ||
      !state.promotions ||
      !Array.isArray(state.promotions.promotion_discount) ||
      state.promotions.promotion_discount.length === 0
    ) {
      return null
    }

    const discounts = state.promotions.promotion_discount

    for (const promo of discounts) {
      if (!Array.isArray(promo.discount_items)) continue

      const matchingItem = promo.discount_items.find(
        s => s.product_id === productId && s.uom_id === uomId && quantity >= s.quantity && s.guest_level_id === guest_level_id
      )

      if (matchingItem) {
        const { discount_items, ...promoData } = promo
        return {
          ...promoData,
          discount_type: matchingItem.discount_type || '%',
          discount_rate: matchingItem.discount_rate || promo.promo_discount_value,
          quantity_required: matchingItem.quantity
        }
      }
    }

    return null
  },
  getPromotionDiscountDisplay: state => (productId, uomId, guest_level_id = 1) => {
    if (!productId || !state.promotions || !Array.isArray(state.promotions.promotion_discount)) {
      return null
    }

    const discounts = state.promotions.promotion_discount
    if (discounts.length === 0) return null

    for (const promo of discounts) {
      const item = promo.discount_items.find(s => s.product_id === productId && s.uom_id === uomId && s.guest_level_id === guest_level_id)
      if (item) {
        const discountRate = item.discount_rate || promo.promo_discount_value
        const discountType = item.discount_type || '%'
        return `Buy ${item.quantity} get ${discountRate}${discountType} discount`
      }
    }

    return null
  },
  getPromotionItemFreeItem: state => (productId, uomId, quantity) => {
    if (
      !productId ||
      !uomId ||
      !quantity ||
      !state.promotions ||
      !Array.isArray(state.promotions.promotion_buy_item_free_item)
    ) {
      return null
    }

    const promotions = state.promotions.promotion_buy_item_free_item
    if (promotions.length === 0) return null

    // loop through promotions
    for (const promo of promotions) {
      if (!Array.isArray(promo.free_items)) continue

      const freeItem = promo.free_items.find(
        f => f.product_buy_id === productId && f.uom_buy_id === uomId
      )

      if (freeItem && Array.isArray(freeItem.free_item_list)) {
        // calculate free items based on quantity
        return freeItem.free_item_list.map(el => ({
          product_id: el.product_free_id,
          unit_price: el.price,
          condition_type: freeItem.condition_type,
          quantity: Math.floor(quantity / freeItem.buy_quantity) * el.free_quantity,
          uom_id: el.uom_free_id,
          is_allow_adjust_free_quantity: freeItem.is_allow_adjust_free_quantity,
          promotions: promo
        }))
      }
    }

    return null
  },
  getPromotionBuyItemFreeItem: state => (productId, uomId) => {
    if (!productId || !state.promotions || !Array.isArray(state.promotions.promotion_buy_item_free_item)) {
      return null
    }

    const promotions = state.promotions.promotion_buy_item_free_item
    if (promotions.length === 0) return null

    // loop through promotions and check free_items
    for (const promo of promotions) {
      if (!Array.isArray(promo.free_items)) continue

      const freeItem = promo.free_items.find(
        f => f.product_buy_id === productId && f.uom_buy_id === uomId
      )

      if (freeItem) {
        return {
          free_item: freeItem,
          promotion_name: promo.promotion_name
        }
      }
    }

    return null
  },
  getPromotionFreeItemPerAmount: state => amount => {
    if (
      !amount ||
      !state.promotions ||
      !Array.isArray(state.promotions.promotion_free_item_per_amount)
    ) {
      return null
    }

    const promotions = state.promotions.promotion_free_item_per_amount
    if (promotions.length === 0) return null

    // loop through promotions
    for (const promo of promotions) {
      if (!Array.isArray(promo.conditions)) continue

      // find a matching condition
      const condition = promo.conditions.find(c => {
        if (c.operator_sign === ">=") {
          return amount >= c.amount_from
        } else if (c.operator_sign === "=") {
          return amount === c.amount_from
        } else {
          return _.inRange(amount, c.amount_from, c.amount_to)
        }
      })

      if (condition) {
        const { operator_sign, ...cleanCondition } = condition
        const { promo_start_date, promo_end_date, ...cleanPromo } = promo

        return {
          ...cleanPromo,
          conditions: cleanCondition
        }
      }
    }

    return null
  },
}

export const mutations = {
  ...baseMutations,
  SET_PRODUCT_CATEGORY(state, data) {
    state.productCategories = data
  },
  RECEIVE_PRODUCTS(state, data) {
    state.products = data
  },
  SET_SYSTEM_SETTING(state, data) {
    state.systemSettings = data
  },
  SET_PROMOTIONS(state, data) {
    state.promotions = data
  },
  SET_COMPANY(state, data) {
    state.companyInfo = data
  },
  RECEIVE_TABLE_SELECTED(state, data) {
    state.tableSelected = data
  }
}

export const actions = {
  fetch(context, attrubutes = {}) {
    let params = Object.assign(
      {
        page: context.state.pagination.page,
        perPage: context.state.pagination.perPage,
        filter: {
          search: context.state.search
        }
      },
      attrubutes
    )
    return CustomerOrder.get({ params }).then(response => {
      context.commit('RECEIVE_RESOURCES', response)
      return Promise.resolve(response)
    })
  },
  store(context, data) {
    return CustomerOrder.store(data)
  },
  async fetchBefore(context) {
    if (isFetchingBefore) {
      return Promise.resolve() // don’t run again
    }

    isFetchingBefore = true

    try {
      // fetch categories only if not loaded
      if (context.state.productCategories.length === 0) {
        let category = await CustomerOrder.category()
        context.commit('SET_PRODUCT_CATEGORY', category.data)
      }

      // always refresh system settings
      let systemSetting = await CustomerOrder.fetchSystemSettings()
      context.commit('SET_SYSTEM_SETTING', systemSetting.data)

      // fetch company info if empty
      if (_.isEmpty(context.state.companyInfo)) {
        let companyInfo = await CustomerOrder.fetchCompanyInfo()
        context.commit('SET_COMPANY', companyInfo.data)
      }

      // always refresh promotions
      if (_.isEmpty(context.state.promotions)) {
        let promotions = await CustomerOrder.fetchPromotions()
        context.commit('SET_PROMOTIONS', promotions.data)
      }

    } catch (error) {
      throw error
    } finally {
      isFetchingBefore = false // release lock
    }

    return Promise.resolve()
  },
  fetchProducts(context, attributes) {
    if (context.state.products.length > 0) {
      return Promise.resolve(context.state.products)
    }

    if (fetchProductsPromise) {
      return fetchProductsPromise // return the same promise
    }

    fetchProductsPromise = CustomerOrder.product({ params: attributes })
      .then(response => {
        context.commit('RECEIVE_PRODUCTS', response.data)
        return response
      })
      .finally(() => {
        fetchProductsPromise = null
      })

    return fetchProductsPromise
  },
  deleteItemOrdered(context, attributes) {
    return CustomerOrder.deleteItemOrdered({
      params: {
        stock_out_id: attributes.stock_out_id,
        product_id: attributes.product_id,
        uom_id: attributes.uom_id,
        quantity: attributes.quantity,
        order_status: attributes.order_status,
        ref_table_id: attributes.ref_table_id,
      }
    }).then(response => {
      return Promise.resolve(response.data)
    })
  },
  fetchBusinessUnit(context, attributes) {
    return CustomerOrder.fetchBusinessUnit({ params: attributes })
  },
  assignBusinessUnit(context, data) {
    return CustomerOrder.assignBusinessUnit(data)
  },
}
